<template>
  <v-row>
    <v-col cols="8">
      <v-data-table-server :headers="headers"
                           :items="clinicalTrials"
                           v-model:items-per-page="itemsPerPage"
                           v-model:sort-by="sortBy"
                           :items-length="totalItems"
                           :loading="loading"
                           :search="search"
                           item-value="nctId"
                           :row-props="rowProps"
                           :items-per-page-options="[15,25,50,100]"
                           :multi-sort="true"
                           @click:row="handleRowClicked"
                           @update:options="loadItems">
        <template v-slot:top>
          <v-toolbar color="secondary" flat>
            <v-spacer></v-spacer>
            <v-btn variant="plain" icon="mdi-file-excel" :disabled="loading || noFiltersActive()" @click.stop="downloadExcel"></v-btn>

            <v-dialog v-model="dialog" max-width="50%">
              <v-card ref="clinical-trial-details">
                <v-card-title>
                  <span class="text-h5">Clinical Trial Details</span>
                  <v-row no-gutters>
                    <v-col class="text-right">
                      <vue-to-print :content="() => this.$refs['clinical-trial-details'].$el"
                                    :document-title="currentItem.name + '-' + 'clinical-trial-details'"
                                    remove-after-print>
                        <template v-slot:trigger>
                          <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                        </template>
                      </vue-to-print>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <h2>{{currentItem.briefTitle}}</h2>
                  <br/>
                  <h3>Official title</h3>
                  <p>{{currentItem.officialTitle}}</p>
                  <br/>
                  <h3>Organization</h3>
                  <p>{{currentItem.organizationFullName}}</p>
                  <br/>
                  <h3>Lead sponsor</h3>
                  <p>{{currentItem.leadSponsorName}}</p>
                  <br/>
                  <div v-if="currentItem.collaborators?.length > 0">
                    <h3>Collaborators</h3>
                    <p>
                      <span v-for="collaborator in [...new Set(currentItem.collaborators)].map(c => c.name).sort()" :key="collaborator">
                        <p>{{collaborator}}</p>
                        <br/>
                      </span>
                    </p>
                  </div>
                  <div v-if="currentItem.centralContacts?.length > 0">
                    <h3>Contacts</h3>
                    <p>
                      <span v-for="contact in currentItem.centralContacts" :key="contact">
                        <p><em>Name:</em> {{contact.name}}</p>
                        <p><em>Phone:</em> {{contact.phone}}</p>
                        <p><em>Email:</em> {{contact.email}}</p>
                        <br/>
                      </span>
                    </p>
                  </div>
                  <h3>Organization study ID</h3>
                  <p>{{currentItem.orgStudyId}}</p>
                  <br/>
                  <h3>MeSH terms</h3>
                  <p>{{currentItem.meshIdsAndNames ? [...new Set(currentItem.meshIdsAndNames.map(m => m.name))].sort().join('; ') : []}}</p>
                  <br/>
                  <h3>Countries</h3>
                  <p>{{currentItem.countries ? [...new Set(currentItem.countries)].sort().join(', ') : []}}</p>
                  <br/>
                  <h3>Phases</h3>
                  <p>{{currentItem.phases ? [...new Set(currentItem.phases)].map(phase => translatePhase(phase).title).sort().join(', ') : []}}</p>
                  <br/>
                  <h3>Overall status</h3>
                  <p>{{translateOverallStatus(currentItem.overallStatus).title}}</p>
                  <br/>
                  <h3>Start date</h3>
                  <p>{{currentItem.startDate}}</p>
                  <br/>
                  <h3>Completion date</h3>
                  <p>{{currentItem.completionDate}}</p>
                  <br/>
                  <h3>Last update posted</h3>
                  <p>{{currentItem.lastUpdatePostDate}}</p>
                  <br/>
                  <h3>Enrollment</h3>
                  <p>{{currentItem.enrollmentCount}} ({{translateEnrollmentCountType(currentItem.enrollmentCountType)}})</p>
                  <br/>
                  <h3>Brief summary</h3>
                  <p>{{currentItem.briefSummary}}</p>
                  <br/>
                  <p><em>The source of this data is ClinicalTrials.gov. It was last fetched at
                    {{formatTimestamp(currentItem.downloadedAt)}}.</em></p>
                </v-card-text>
                <v-card-actions>
                  <v-btn variant="plain" @click.stop="openClinicalTrial(currentItem.nctId)">ClinicalTrials.gov ID {{currentItem.nctId}}</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn variant="text" @click="close" :disabled="false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:[`item.organizationFullName`]="{ item }">
          <v-chip-group :column="true">
            <v-chip class="me-2" color="primary" size="small" :label="true">{{ item.organizationFullName }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.meshIdsAndNames`]="{ item }">
          <v-chip v-for="meshTerm in [...new Set(item.meshIdsAndNames.map(m => m.name))].sort()"
                  :key="meshTerm" class="me-2"
                  color="primary" size="small" :label="true">{{ meshTerm }}</v-chip>
        </template>
        <template v-slot:[`item.overallStatus`]="{ item }">
          <v-chip-group :column="true">
            <v-chip class="me-2" color="primary" size="small" :label="true">{{ translateOverallStatus(item.overallStatus).title }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.countries`]="{ item }">
          <v-chip-group :column="true">
            <v-chip v-for="country in [...new Set(item.countries)].sort()" :key="country" class="me-2"
                    color="primary" size="small" :label="true">{{ country }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.phases`]="{ item }">
          <v-chip-group :column="true">
            <v-chip v-for="phase in [...new Set(item.phases)].sort()" :key="phase" class="me-2"
                    color="primary" size="small" :label="true">{{ translatePhase(phase).title }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:no-data>
          <v-card-text>No data available</v-card-text>
        </template>
      </v-data-table-server>
    </v-col>
    <v-col cols="4">
      <v-toolbar color="secondary" flat>
        <v-spacer></v-spacer>
        <v-label>Filters</v-label>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Geographical distribution">
            <div>
              <GChart :settings="{ packages: ['geochart'], mapsApiKey: 'AIzaSyC95Y92u-OHlj8KHIODdWMCTqZe0WqZXZ4' }"
                      type="GeoChart"
                      @ready="regionalMapChartReady"
                      :data="regionalMapChartData"
                      :options="regionalMapChartOptions"
                      v-if="regionalMapChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div class="number-box-left">
              <v-data-table :headers="meshTermHeaders"
                            :items="meshTermCounts"
                            :loading="loading"
                            :fixed-header="true"
                            :row-props="(meshIds?.length !== 0) ? () => {} : rowProps"
                            height="400px"
                            :items-per-page="0"
                            @click:row="handleMeshTermCountsRowClicked">
                <template #bottom></template>
              </v-data-table>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="overallStatusChartReady"
                      :data="overallStatusChartData"
                      :options="overallStatusChartOptions"
                      v-if="overallStatusChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="organizationsChartReady"
                      :data="organizationsChartData"
                      :options="organizationsChartOptions"
                      v-if="organizationsChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="phasesChartReady"
                      :data="phasesChartData"
                      :options="phasesChartOptions"
                      v-if="phasesChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500" v-if="visible">
    <v-card class="mx-auto" elevation="0">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="clinical_trials_filter_form">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete v-model="selectedMeshTerms"
                                  :items="meshTermItems"
                                  :loading="meshTermItems.length === 0"
                                  v-model:search="meshTermSearch"
                                  :rules="[rules.required]"
                                  label="MeSH terms"
                                  placeholder="Select MeSH terms"
                                  :disabled="meshIds?.length !== 0"
                                  return-object chips multiple hide-no-data clear-on-select>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete v-model="selectedOrganizations"
                                  :items="organizationItems"
                                  :loading="organizationItems.length === 0"
                                  v-model:search="organizationSearch"
                                  :rules="[rules.required]"
                                  label="Organization or collaborator"
                                  placeholder="Select organization/collaborator"
                                  return-object chips multiple hide-no-data clear-on-select>
                  </v-autocomplete>

                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-combobox v-model="selectedOverallStatus" :items="overallStatusItems"
                              v-if="overallStatusItems.length > 0"
                              label="Overall status" :rules="[rules.required]" @click="$refs.clinical_trials_filter_form.validate()"
                              chips multiple></v-combobox>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-combobox v-model="selectedCountries"
                              :items="countryItems"
                              v-if="countryItems.length > 0"
                              label="Country"
                              :rules="[rules.required]"
                              @click="$refs.clinical_trials_filter_form.validate()"
                              return-object chips multiple>
                    <template #item="{ props }">
                      <v-list-subheader v-if="props.header" @click="toggleAllSomeCountries(props.children)">
                        {{ props.header }}
                      </v-list-subheader>
                      <v-list-subheader class="ms-2" v-else-if="props.subheader" @click="toggleAllSomeCountries(props.children)">
                        {{ props.subheader }}
                      </v-list-subheader>
                      <v-divider v-else-if="props.divider" class="mt-2" />
                      <v-list-item class="ms-4" v-else v-bind="props"></v-list-item>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-combobox v-model="selectedPhases" :items="phaseItems"
                              v-if="phaseItems.length > 0"
                              label="Phase" :rules="[rules.required]" @click="$refs.clinical_trials_filter_form.validate()"
                              chips multiple></v-combobox>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="clinicalTrialsSearch"
                                @update:model-value="clinicalTrialsSearchUpdated"
                                label="Search"
                                placeholder="Search"
                                hide-details filled dense></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import {GChart} from 'vue-google-charts'
import { VueToPrint } from "vue-to-print";
import country from 'country-list-js';

const ANY = 'Any'

const ANY_MESH_TERM = {
  title: 'Any',
  value: ANY
}

const ANY_ORGANIZATION = {
  title: 'Any',
  value: ANY
}

const ANY_COUNTRY = {
  title: 'Any',
  value: ANY
}

const STATUS_ACTIVE_NOT_RECRUITING = {
  title: 'Active, not recruiting',
  value: 'ACTIVE_NOT_RECRUITING'
}

const STATUS_APPROVED_FOR_MARKETING = {
  title: 'Approved for marketing',
  value: 'APPROVED_FOR_MARKETING'
}

const STATUS_AVAILABLE = {
  title: 'Available',
  value: 'AVAILABLE'
}

const STATUS_COMPLETED = {
  title: 'Completed',
  value: 'COMPLETED'
}

const STATUS_ENROLLING_BY_INVITATION = {
  title: 'Enrolling by invitation',
  value: 'ENROLLING_BY_INVITATION'
}

const STATUS_NO_LONGER_AVAILABLE = {
  title: 'No longer available',
  value: 'NO_LONGER_AVAILABLE'
}

const STATUS_NOT_YET_RECRUITING = {
  title: 'Not yet recruiting',
  value: 'NOT_YET_RECRUITING'
}

const STATUS_RECRUITING = {
  title: 'Recruiting',
  value: 'RECRUITING'
}

const STATUS_SUSPENDED = {
  title: 'Suspended',
  value: 'SUSPENDED'
}

const STATUS_TEMPORARILY_NOT_AVAILABLE = {
  title: 'Temporarily not available',
  value: 'TEMPORARILY_NOT_AVAILABLE'
}

const STATUS_TERMINATED = {
  title: 'Terminated',
  value: 'TERMINATED'
}

const STATUS_UNKNOWN = {
  title: 'Unknown',
  value: 'UNKNOWN'
}

const STATUS_WITHDRAWN = {
  title: 'Withdrawn',
  value: 'WITHDRAWN'
}

const STATUS_WITHHELD = {
  title: 'Withheld',
  value: 'WITHHELD'
}

const PHASE_NA = {
  title: 'Not Applicable',
  value: 'NA'
}

const PHASE_EARLY_PHASE1 = {
  title: 'Early Phase 1',
  value: 'EARLY_PHASE1'
}

const PHASE_PHASE1 = {
  title: 'Phase 1',
  value: 'PHASE1'
}

const PHASE_PHASE2 = {
  title: 'Phase 2',
  value: 'PHASE2'
}

const PHASE_PHASE3 = {
  title: 'Phase 3',
  value: 'PHASE3'
}

const PHASE_PHASE4 = {
  title: 'Phase 4',
  value: 'PHASE4'
}

const UNCLASSIFIED_COUNTRIES = {
  'Bonaire, Sint Eustatius and Saba': ['North America', 'Caribbean'],
  'Brunei Darussalam': ['Asia', 'South-East Asia'],
  'Congo': ['Africa', 'Central Africa'],
  'Congo, The Democratic Republic of the': ['Africa', 'Central Africa'],
  'Côte D\'Ivoire': ['Africa', 'Western Africa'],
  'Czechia': ['Europe', 'Central Europe'],
  'Federated States of Micronesia': ['Oceania', 'Oceania'],
  'Former Serbia and Montenegro': ['Europe', 'Balkans'],
  'Former Yugoslavia': ['Europe', 'Balkans'],
  'Ghana': ['Africa', 'Western Africa'],
  'Holy See (Vatican City State)': ['Europe', 'Southern Europe'],
  'Iran, Islamic Republic of': ['Asia', 'Western Asia, Middle East'],
  'Korea, Republic of': ['Asia', 'East Asia'],
  'Lao People\'s Democratic Republic': ['Asia', 'South-East Asia'],
  'Libyan Arab Jamahiriya': ['Africa', 'Northern Africa'],
  'Macau': ['Asia', 'East Asia'],
  'Macedonia, The Former Yugoslav Republic of': ['Europe', 'Balkans'],
  'Moldova, Republic of': ['Europe', 'Central Europe'],
  'Netherlands': ['Europe', 'Western Europe'],
  'Netherlands Antilles': ['North America', 'Caribbean'],
  'North Macedonia': ['Europe', 'Balkans'],
  'Palestinian Territories, Occupied': ['Asia', 'Western Asia, Middle East'],
  'Palestinian Territory, occupied': ['Asia', 'Western Asia, Middle East'],
  'Réunion': ['Africa', 'East Africa, Southern Africa'],
  'Russian Federation': ['Europe', 'Eastern Europe'],
  'Syrian Arab Republic': ['Asia', 'Western Asia, Middle East'],
  'Timor-Leste': ['Asia', 'South-East Asia, Oceania'],
  'Virgin Islands (U.S.)': ['North America', 'Caribbean']
}

function debounce(func, timer, timeout = 1000) {
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

function addItemsIfNotExist(array, items) {
  let arrayCopy = [...array];
  if (!Array.isArray(items)) {
    items = [items];
  }
  items.forEach(item => {
    if (!array.some(a => a.value === item.value)) {
      arrayCopy.push(item);
    }
  });
  arrayCopy.sort()
  return arrayCopy
}

function removeItemsIfExist(array, items) {
  let arrayCopy = [...array];
  if (!Array.isArray(items)) {
    items = [items];
  }
  items.forEach(item => {
    array.forEach(a => {
      if (a.value === item.value) {
        let index = arrayCopy.indexOf(a);
        arrayCopy.splice(index, 1);
      }
    })
  });
  arrayCopy.sort()
  return arrayCopy
}

export default defineComponent({
  name: 'ClinicalTrials',
  props: {
    meshIds: Array,
    meshDescription: String,
    visible: {
      type: Boolean,
      default: true
    }
  },
  components: {
    GChart,
    VueToPrint
  },
  data: (context) => ({
    filter_drawer: false,
    dialog: false,
    itemsPerPage: 15,
    sortBy: [{ key: 'startDate', order: 'desc' }],
    search: '',
    loading: true,
    totalItems: 0,
    currentItem: {},
    headers: [
      {
        title: 'Brief title',
        align: 'start',
        sortable: true,
        key: 'briefTitle',
        width: '30%'
      },
      {
        title: 'Organization',
        key: 'organizationFullName',
        width: '10%'
      },
      {
        title: 'Study ID',
        key: 'orgStudyId',
        width: '10%'
      },
      {
        title: 'MeSH terms',
        sortable: false,
        key: 'meshIdsAndNames',
        width: '10%'
      },
      {
        title: 'Start date',
        key: 'startDate',
        width: '5%'
      },
      {
        title: 'Overall status',
        key: 'overallStatus',
        width: '10%'
      },
      {
        title: 'Completion date',
        key: 'completionDate',
        width: '5%'
      },
      {
        title: 'Countries',
        sortable: false,
        key: 'countries',
        width: '10%'
      },
      {
        title: 'Phases',
        sortable: false,
        key: 'phases',
        width: '10%'
      }
    ],
    clinicalTrials: [],
    overallStatusItems: [
      STATUS_ACTIVE_NOT_RECRUITING,
      STATUS_APPROVED_FOR_MARKETING,
      STATUS_AVAILABLE,
      STATUS_COMPLETED,
      STATUS_ENROLLING_BY_INVITATION,
      STATUS_NO_LONGER_AVAILABLE,
      STATUS_NOT_YET_RECRUITING,
      STATUS_RECRUITING,
      STATUS_SUSPENDED,
      STATUS_TEMPORARILY_NOT_AVAILABLE,
      STATUS_TERMINATED,
      STATUS_UNKNOWN,
      STATUS_WITHDRAWN,
      STATUS_WITHHELD
    ],
    selectedOverallStatus: [ANY],
    countryItems: [ANY_COUNTRY],
    selectedCountries: [ANY_COUNTRY],
    phaseItems: [
      PHASE_NA,
      PHASE_EARLY_PHASE1,
      PHASE_PHASE1,
      PHASE_PHASE2,
      PHASE_PHASE3,
      PHASE_PHASE4
    ],
    selectedPhases: [ANY],
    meshTermItems: [ANY_MESH_TERM],
    selectedMeshTerms: context.meshIds?.length > 0 ? [{
      title: context.meshDescription,
      value: context.meshIds
    }] : [ANY_MESH_TERM],
    meshTermSearch: '',
    meshTermSearchTimer: undefined,
    organizationItems: [ANY_ORGANIZATION],
    selectedOrganizations: [ANY_ORGANIZATION],
    organizationSearch: '',
    organizationSearchTimer: undefined,
    clinicalTrialsSearch: '',
    clinicalTrialsSearchTimer: undefined,
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    regionalMapChartData: [],
    regionalMapChartOptions: {
      datalessRegionColor: '#647e91',
      defaultColor: '#647e91',
      backgroundColor: '#364c5b',
      colorAxis: {colors: ['#a9a868', '#b2df8a']},
      height: 800,
      legend: 'none'
    },
    overallStatusChartData: [],
    overallStatusChartOptions: {
      title: 'Overall status',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    meshTermHeaders: [
      {
        title: 'MeSH term',
        align: 'start',
        key: 'meshTerm',
      },
      {
        title: 'Number of clinical trials',
        align: 'start',
        key: 'count',
      }],
    meshTermCounts: [],
    organizationsChartData: [],
    organizationsChartOptions: {
      title: 'Organizations/collaborators',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    phasesChartData: [],
    phasesChartOptions: {
      title: 'Phase',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    }
  }),
  watch: {
    meshIds() {
      this.selectedMeshTerms = this.meshIds?.length > 0 ? [{
        title: this.meshDescription,
        value: this.meshIds
      }] : [ANY_MESH_TERM]
      this.filterClinicalTrials()
    },
    selectedOverallStatus(newSelectedOverallStatus, oldSelectedOverallStatus) {
      if (((newSelectedOverallStatus.length === 1) && (newSelectedOverallStatus[0] === ANY)) ||
          (!newSelectedOverallStatus.includes(ANY) && !oldSelectedOverallStatus.includes(ANY))) {
        this.filterClinicalTrials()
        return
      }

      if (newSelectedOverallStatus.includes(ANY) && !oldSelectedOverallStatus.includes(ANY)) {
        this.selectedOverallStatus = [ANY]
      } else if (newSelectedOverallStatus.includes(ANY) && oldSelectedOverallStatus.includes(ANY)) {
        this.selectedOverallStatus = this.selectedOverallStatus.filter(c => c !== ANY)
      }
      this.filterClinicalTrials()
    },
    selectedCountries(newSelectedCountries, oldSelectedCountries) {
      newSelectedCountries = newSelectedCountries.map(o => o.value)
      oldSelectedCountries = oldSelectedCountries.map(o => o.value)
      if (((newSelectedCountries.length === 1) && (newSelectedCountries[0] === ANY)) ||
          (!newSelectedCountries.includes(ANY) && !oldSelectedCountries.includes(ANY))) {
        this.filterClinicalTrials()
        return
      }

      if (newSelectedCountries.includes(ANY) && !oldSelectedCountries.includes(ANY)) {
        this.selectedCountries = [ANY_COUNTRY]
      } else if (newSelectedCountries.includes(ANY) && oldSelectedCountries.includes(ANY)) {
        this.selectedCountries = this.selectedCountries.filter(c => c.value !== ANY)
      }
      this.filterClinicalTrials()
    },
    selectedPhases(newSelectedPhases, oldSelectedPhases) {
      if (((newSelectedPhases.length === 1) && (newSelectedPhases[0] === ANY)) ||
          (!newSelectedPhases.includes(ANY) && !oldSelectedPhases.includes(ANY))) {
        this.filterClinicalTrials()
        return
      }

      if (newSelectedPhases.includes(ANY) && !oldSelectedPhases.includes(ANY)) {
        this.selectedPhases = [ANY]
      } else if (newSelectedPhases.includes(ANY) && oldSelectedPhases.includes(ANY)) {
        this.selectedPhases = this.selectedPhases.filter(c => c !== ANY)
      }
      this.filterClinicalTrials()
    },
    selectedMeshTerms(newSelectedMeshTerms, oldSelectedMeshTerms) {
      newSelectedMeshTerms = newSelectedMeshTerms.map(o => o.value)
      oldSelectedMeshTerms = oldSelectedMeshTerms.map(o => o.value)
      if (((newSelectedMeshTerms.length === 1) && (newSelectedMeshTerms[0] === ANY)) ||
          (!newSelectedMeshTerms.includes(ANY) && !oldSelectedMeshTerms.includes(ANY))) {
        this.filterClinicalTrials()
        return
      }

      if (newSelectedMeshTerms.includes(ANY) && !oldSelectedMeshTerms.includes(ANY)) {
        this.selectedMeshTerms = [ANY_MESH_TERM]
      } else if (newSelectedMeshTerms.includes(ANY) && oldSelectedMeshTerms.includes(ANY)) {
        this.selectedMeshTerms = this.selectedMeshTerms.filter(o => o.value !== ANY)
      }
      this.filterClinicalTrials()
    },
    selectedOrganizations(newSelectedOrganizations, oldSelectedOrganizations) {
      newSelectedOrganizations = newSelectedOrganizations.map(o => o.value)
      oldSelectedOrganizations = oldSelectedOrganizations.map(o => o.value)
      if (((newSelectedOrganizations.length === 1) && (newSelectedOrganizations[0] === ANY)) ||
          (!newSelectedOrganizations.includes(ANY) && !oldSelectedOrganizations.includes(ANY))) {
        this.filterClinicalTrials()
        return
      }

      if (newSelectedOrganizations.includes(ANY) && !oldSelectedOrganizations.includes(ANY)) {
        this.selectedOrganizations = [ANY_ORGANIZATION]
      } else if (newSelectedOrganizations.includes(ANY) && oldSelectedOrganizations.includes(ANY)) {
        this.selectedOrganizations = this.selectedOrganizations.filter(o => o.value !== ANY)
      }
      this.filterClinicalTrials()
    },
    meshTermSearch() {
      if (!this.meshTermSearch || (this.meshTermSearch.trim() === '')) {
        if (this.meshTermSearchTimer) {
          clearTimeout(this.meshTermSearchTimer)
        }
        this.meshTermItems = [...this.selectedMeshTerms.filter(o => o.value !== ANY)]
        this.meshTermItems.unshift(ANY_MESH_TERM)
        return;
      }
      this.meshTermItems = []
      let meshTermSearch = this.meshTermSearch
      this.meshTermSearchTimer = debounce(() => {
        this.loadClinicalTrialsMeshTerms(meshTermSearch).then((data) => {
          if (data) {
            this.meshTermItems = data.map(meshTerm => {
              return {
                title: meshTerm.title,
                value: [meshTerm.value]
              }
            })
            if (data.length <= 100) {
              this.meshTermItems.unshift({
                title: 'Any matching "' + meshTermSearch + '"',
                value: data.map(meshTerm => meshTerm.value)
              })
            }
            this.meshTermItems.unshift(ANY_MESH_TERM)
          } else {
            this.meshTermItems = [...this.selectedMeshTerms.filter(o => o.value !== ANY)]
            this.meshTermItems.unshift(ANY_MESH_TERM)
          }
        })
      }, this.meshTermSearchTimer)()
    },
    organizationSearch() {
      if (!this.organizationSearch || (this.organizationSearch.trim() === '')) {
        if (this.organizationSearchTimer) {
          clearTimeout(this.organizationSearchTimer)
        }
        this.organizationItems = [...this.selectedOrganizations.filter(o => o.value !== ANY)]
        this.organizationItems.unshift(ANY_ORGANIZATION)
        return;
      }
      this.organizationItems = []
      let organizationSearch = this.organizationSearch
      this.organizationSearchTimer = debounce(() => {
        this.loadClinicalTrialsOrganizations(organizationSearch).then((data) => {
          if (data) {
            this.organizationItems = data.map(organization => {
              return {
                title: organization,
                value: organization
              }
            })
            if (organizationSearch.length >= 3) {
              this.organizationItems.unshift({
                title: 'Any matching "' + organizationSearch + '"',
                value: '*' + organizationSearch + '*'
              })
            }
            this.organizationItems.unshift(ANY_ORGANIZATION)
          } else {
            this.organizationItems = [...this.selectedOrganizations.filter(o => o.value !== ANY)]
            this.organizationItems.unshift(ANY_ORGANIZATION)
          }
        })
      }, this.organizationSearchTimer)()
    }
  },
  methods: {
    async loadClinicalTrials(page, itemsPerPage, sortBy) {
      let data = axios.get('/clinical-trials',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              mesh_ids: this.selectedMeshTerms.some(t => t.value === ANY) ? undefined : this.selectedMeshTerms.map(o => o.value).join(","),
              organization: this.selectedOrganizations.some(o => o.value === ANY) ? undefined :
                  this.selectedOrganizations.map(o => o.value).join(";"),
              overall_status: this.selectedOverallStatus.includes(ANY) ? undefined : this.selectedOverallStatus.map(status => status.value).join(","),
              country: this.selectedCountries.some(c => c.value === ANY) ? undefined : this.selectedCountries.map(c => c.value).join(";"),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.map(phase => phase.value).join(","),
              search: this.clinicalTrialsSearch === '' ? undefined : this.clinicalTrialsSearch,
              page: page,
              items_per_page: itemsPerPage,
              sort_by: sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            }
          });
      return data.then((response) => {
        return {
          items: response.data.items,
          total: response.data.total
        }
      });
    },

    async loadClinicalTrialsMetaData() {
      return axios.get('/clinical-trials-meta-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              mesh_ids: this.selectedMeshTerms.some(t => t.value === ANY) ? undefined : this.selectedMeshTerms.map(o => o.value).join(","),
              organization: this.selectedOrganizations.some(o => o.value === ANY) || this.selectedOrganizations.length === 0 ? undefined :
                  this.selectedOrganizations.map(o => o.value).join(";"),
              overall_status: this.selectedOverallStatus.includes(ANY) ? undefined : this.selectedOverallStatus.map(status => status.value).join(","),
              country: this.selectedCountries.some(c => c.value === ANY) ? undefined : this.selectedCountries.map(c => c.value).join(";"),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.map(phase => phase.value).join(","),
              search: this.clinicalTrialsSearch === '' ? undefined : this.clinicalTrialsSearch
            }
          });
    },

    async loadClinicalTrialsMeshTerms(search) {
      return axios.get('/clinical-trials-mesh-terms',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              search: search
            }
          })
          .then((response) => {
            return response.data;
          });
    },

    async loadClinicalTrialsOrganizations(search) {
      return axios.get('/clinical-trials-organizations',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              search: search
            }
          })
          .then((response) => {
            return response.data;
          });
    },

    downloadExcel() {
      this.loading = true
      axios.get('/clinical-trials-excel',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              mesh_ids: this.selectedMeshTerms.some(t => t.value === ANY) ? undefined : this.selectedMeshTerms.map(o => o.value).join(","),
              organization: this.selectedOrganizations.some(o => o.value === ANY) ? undefined :
                  this.selectedOrganizations.map(o => o.value).join(";"),
              overall_status: this.selectedOverallStatus.includes(ANY) ? undefined : this.selectedOverallStatus.map(status => status.value).join(","),
              country: this.selectedCountries.some(c => c.value === ANY) ? undefined : this.selectedCountries.map(c => c.value).join(";"),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.map(phase => phase.value).join(","),
              search: this.clinicalTrialsSearch === '' ? undefined : this.clinicalTrialsSearch,
              sort_by: this.sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'clinical-trials.xlsx');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.loading = false
          });
    },

    async loadStaticData() {
      return await Promise.all([
        this.loadClinicalTrialsMetaData()
      ]).then((response) => {
        this.overallStatusItems = response[0].data.allOverallStatuses.map(status => this.translateOverallStatus(status));
        this.overallStatusItems.unshift(ANY)

        let countryItems = []
        this.countriesToContinentAndRegionList(response[0].data.allCountries).forEach(continent => {
          let countries = []
          for (let regionValue of continent.children) {
            countries.push(...regionValue.children)
          }
          countryItems.push({ props: { header: continent.value, children: countries.map(c => ({
                title: c,
                value: c
              }))
            }})
          let regions = [...continent.children]
          regions.forEach(region => {
            countryItems.push({ props: { subheader: region.value, children: region.children.map(c => ({
                  title: c,
                  value: c
                }))
              }})
            region.children.forEach(c => {
              countryItems.push({
                title: c,
                value: c
              })})
          })

          countryItems.push({ props: { divider: true }})
        })
        this.countryItems = countryItems

        this.countryItems.unshift(ANY_COUNTRY)
        this.phaseItems = response[0].data.allPhases.map(phase => this.translatePhase(phase));
        this.phaseItems.unshift(ANY)
        this.meshTermCounts = response[0].data.meshTermCounts;
        this.overallStatusChartData = response[0].data.overallStatusCounts
        this.organizationsChartData = response[0].data.organizationCounts;
        this.regionalMapChartData = response[0].data.countryCounts;
        this.phasesChartData = response[0].data.phaseCounts;
        console.log('Static data loaded: ClinicalTrials')
      })
    },

    loadItems({ page, itemsPerPage, sortBy }) {
      this.loading = true
      return Promise.all([this.loadClinicalTrials(page, itemsPerPage, sortBy).then(({items, total}) => {
        this.clinicalTrials = items;
        this.totalItems = total;
      }), this.loadClinicalTrialsMetaData().then((response => {
        this.meshTermCounts = response.data.meshTermCounts;
        this.overallStatusChartData = response.data.overallStatusCounts
        this.organizationsChartData = response.data.organizationCounts;
        this.regionalMapChartData = response.data.countryCounts;
        this.phasesChartData = response.data.phaseCounts;
      }))]).finally(() => {
        this.loading = false
        console.log('Clinical trials loaded')
      })
    },

    filterClinicalTrials() {
      // Triggers an event for searching
      this.search = String(Date.now())
    },

    openClinicalTrial(nctId) {
      window.open('https://clinicaltrials.gov/study/' + nctId, '_blank')
    },

    rowProps() {
      return {
        class: "ciencia_row"
      };
    },

    handleRowClicked(event, row) {
      this.currentItem = row.item
      this.dialog = true
    },

    handleMeshTermCountsRowClicked(event, row) {
      if ((!this.meshIds || (this.meshIds.length === 0)) && row.item) {
        this.toggleAllOneMeshTerms(row.item.meshTerm, row.item.meshId)
      }
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.currentItem = {}
      })
    },

    countriesToContinentAndRegionList(countries) {
      let countryMap = new Map()
      countries.forEach(c => {

        let countryMatch = country.findByName(c)
        let continent = this.findNonClassifiedContinent(c) || countryMatch?.continent || 'Non-classified continent'
        let region = this.findNonClassifiedRegion(c) || countryMatch?.region || 'Non-classified region'

        if (countryMap.has(continent)) {
          let cont = countryMap.get(continent)
          if (cont.children.has(region)) {
            let reg = cont.children.get(region)
            reg.children.push(c)
          } else {
            cont.children.set(region, {
              value: region,
              children: [c]
            })
          }
        } else {
          let cont = {
            value: continent,
            children: new Map()
          }
          cont.children.set(region, {
            value: region,
            children: [c]
          })
          countryMap.set(continent, cont)
        }
      })

      for (let [, continent] of countryMap) {
        for (let [, region] of continent.children) {
          region.children.sort()
        }
        continent.children = Array.from(continent.children.values())
            .sort((r1, r2) => r1.value < r2.value ? -1 : 1)
      }

      return Array.from(countryMap.values()).sort((c1, c2) => c1.value < c2.value ? -1 : 1)
    },

    findNonClassifiedContinent(country) {
      if (UNCLASSIFIED_COUNTRIES[country]) {
        return UNCLASSIFIED_COUNTRIES[country][0]
      }
      return undefined
    },

    findNonClassifiedRegion(country) {
      if (UNCLASSIFIED_COUNTRIES[country]) {
        return UNCLASSIFIED_COUNTRIES[country][1]
      }
      return undefined
    },

    translateOverallStatus(overallStatus) {
      switch(overallStatus) {
        case 'ACTIVE_NOT_RECRUITING':
          return STATUS_ACTIVE_NOT_RECRUITING
        case 'APPROVED_FOR_MARKETING':
          return STATUS_APPROVED_FOR_MARKETING
        case 'AVAILABLE':
          return STATUS_AVAILABLE
        case 'COMPLETED':
          return STATUS_COMPLETED
        case 'ENROLLING_BY_INVITATION':
          return STATUS_ENROLLING_BY_INVITATION
        case 'NO_LONGER_AVAILABLE':
          return STATUS_NO_LONGER_AVAILABLE
        case 'NOT_YET_RECRUITING':
          return STATUS_NOT_YET_RECRUITING
        case 'RECRUITING':
          return STATUS_RECRUITING
        case 'SUSPENDED':
          return STATUS_SUSPENDED
        case 'TEMPORARILY_NOT_AVAILABLE':
          return STATUS_TEMPORARILY_NOT_AVAILABLE
        case 'TERMINATED':
          return STATUS_TERMINATED
        case 'UNKNOWN':
          return STATUS_UNKNOWN
        case 'WITHDRAWN':
          return STATUS_WITHDRAWN
        case 'WITHHELD':
          return STATUS_WITHHELD
      }
      return {
        title: overallStatus,
        value: overallStatus
      }
    },

    translateEnrollmentCountType(enrollmentCountType) {
      switch(enrollmentCountType) {
        case 'ACTUAL':
          return 'Actual'
        case 'ESTIMATED':
          return 'Estimated'
      }
      return enrollmentCountType
    },

    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '';
      }
      return new Date(timestamp).toLocaleString();
    },

    translatePhase(phase) {
      switch(phase) {
        case 'NA':
          return PHASE_NA
        case 'EARLY_PHASE1':
          return PHASE_EARLY_PHASE1
        case 'PHASE1':
          return PHASE_PHASE1
        case 'PHASE2':
          return PHASE_PHASE2
        case 'PHASE3':
          return PHASE_PHASE3
        case 'PHASE4':
          return PHASE_PHASE4
      }
      return {
        title: phase,
        value: phase
      }
    },

    toggleAllOneMeshTerms(meshTerm, meshId) {
      if ((this.selectedMeshTerms.length === 1) && (this.selectedMeshTerms[0].value === meshId)) {
        this.selectedMeshTerms = [...this.meshTermItems]
      } else {
        this.selectedMeshTerms = [{
          title: meshTerm,
          value: meshId
        }]
      }
    },

    toggleAllOneOrganizations(organization) {
      if ((this.selectedOrganizations.length === 1) && (this.selectedOrganizations[0].value === organization)) {
        this.selectedOrganizations = [...this.organizationItems]
      } else {
        this.selectedOrganizations = [{
          title: organization,
          value: organization
        }]
      }
    },

    toggleAllOneOverallStatus(overallStatus) {
      if ((this.selectedOverallStatus.length === 1) && (this.selectedOverallStatus[0].value === overallStatus)) {
        this.selectedOverallStatus = [...this.overallStatusItems]
      } else {
        this.selectedOverallStatus = [this.translateOverallStatus(overallStatus)]
      }
    },

    toggleAllOneCountries(country) {
      if ((this.selectedCountries.length === 1) && (this.selectedCountries[0].value === country)) {
        this.selectedCountries = [...this.countryItems]
      } else {
        this.selectedCountries = [{
          title: country,
          value: country
        }]
      }
    },

    toggleAllSomeCountries(countries) {
      if (this.selectedCountries.some(selectedCountry => countries.some(c => c.value === selectedCountry.value))) {
        this.selectedCountries = removeItemsIfExist(this.selectedCountries, countries)
      } else {
        this.selectedCountries = addItemsIfNotExist(this.selectedCountries, countries)
      }
    },

    toggleAllOnePhases(phase) {
      if ((this.selectedPhases.length === 1) && (this.selectedPhases[0].value === phase)) {
        this.selectedPhases = [...this.phaseItems]
      } else {
        this.selectedPhases = [this.translatePhase(phase)]
      }
    },

    noFiltersActive() {
      return (!this.selectedMeshTerms || this.selectedMeshTerms.some(o => o.value === ANY)) &&
          (!this.selectedOrganizations || this.selectedOrganizations.some(o => o.value === ANY)) &&
          (!this.selectedOverallStatus || this.selectedOverallStatus.includes(ANY)) &&
          (!this.selectedCountries || this.selectedCountries.some(c => c.value === ANY)) &&
          (!this.selectedPhases || this.selectedPhases.includes(ANY)) &&
          (!this.clinicalTrialsSearch || this.clinicalTrialsSearch.trim() === '')
    },

    clinicalTrialsSearchUpdated() {
      this.clinicalTrialsSearchTimer = debounce(() => this.filterClinicalTrials(), this.clinicalTrialsSearchTimer)()
    },

    async overallStatusChartReady(chart, google) {
      const setOverallStatusChartSelection = (selectedRow) => {
        let selected_overall_status = this.overallStatusChartData[selectedRow + 1][0]
        if (selected_overall_status !== undefined) {
          this.toggleAllOneOverallStatus(selected_overall_status.v)
        }
      }

      const overallStatusSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setOverallStatusChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', overallStatusSelectHandler);
    },

    async organizationsChartReady(chart, google) {
      const setOrganizationsChartSelection = (selectedRow) => {
        let selected_organization = this.organizationsChartData[selectedRow + 1][0]
        if (selected_organization !== undefined) {
          this.toggleAllOneOrganizations(selected_organization)
        }
      }

      const organizationsSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setOrganizationsChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', organizationsSelectHandler);
    },

    async phasesChartReady(chart, google) {
      const setPhasesChartSelection = (selectedRow) => {
        let selected_phase = this.phasesChartData[selectedRow + 1][0]
        if (selected_phase !== undefined) {
          this.toggleAllOnePhases(selected_phase.v)
        }
      }

      const phasesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setPhasesChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', phasesSelectHandler);
    },

    async regionalMapChartReady(chart, google) {
      const setRegionalMapChartSelection = (selectedRow) => {
        let selected_region = this.regionalMapChartData[selectedRow + 1][0].f
        if (selected_region !== undefined) {
          this.toggleAllOneCountries(selected_region)
        }
      }

      const regionSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setRegionalMapChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', regionSelectHandler);
    }
  },
  async mounted() {
    await this.loadStaticData()
  }
});
</script>

<style lang="scss" scoped>
.dp__theme_dark {
  --dp-background-color: #364c5b;
  --dp-text-color: #dcdcdc;
  --dp-border-color: #dcdcdc;
}
.error {
  color: indianred;
}

::v-deep .ciencia_row:hover {
  background-color: #566c7b;
}

.v-list-subheader:hover {
  background-color: #566c7b;
}

.number-box-left {
  height: 450px;
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}
</style>
